import { Actor, Color, Engine, Font, FontUnit, Label, vec, Vector } from "excalibur";
import { gameState } from "./gameState";
import {liveEnemies} from "./enemy";

export class Tower extends Actor {
  maxHp = 200;
  hp = this.maxHp;
  maxMana = 100;
  mana = 0;
  manaRegen = 5; // per second

  attackRate = 2; // shots per second
  attackCooldown = 0;
  range = 260;

  // Slow HP regen: +1 HP every 10 game ticks (independent of timescale)
  private hpRegenTickCounter = 0;

  // Rage state
  private rageActive = false;
  private rageTimeLeftMs = 0;

  constructor(pos: Vector) {
    super({ name: 'tower', pos, width: 24, height: 24, color: Color.Violet });
    // Initialize vitals from saved game state if available
    if (isFinite(gameState.maxHp)) this.maxHp = Math.max(1, Math.floor(gameState.maxHp));
    if (isFinite(gameState.hp)) this.hp = Math.max(0, Math.min(this.maxHp, Math.floor(gameState.hp)));
    if (isFinite(gameState.maxMana)) this.maxMana = Math.max(1, Math.floor(gameState.maxMana));
    if (isFinite(gameState.mana)) this.mana = Math.max(0, Math.min(this.maxMana, Math.floor(gameState.mana)));
    // Ensure game state reflects current values
    gameState.maxHp = this.maxHp;
    gameState.hp = this.hp;
    gameState.maxMana = this.maxMana;
    gameState.mana = this.mana;
  }

  takeDamage(amount: number) {
    this.hp = Math.max(0, this.hp - amount);
    gameState.hp = this.hp;
  }

  isKilled() {
    return this.hp <= 0;
  }

  private findNearestEnemy(engine: Engine) {
    let best: Actor | null = null;
    let bestDistSq = Infinity;
    const sx = this.pos.x, sy = this.pos.y;
    for (const e of liveEnemies) {
      const dx = e.pos.x - sx;
      const dy = e.pos.y - sy;
      const d2 = dx*dx + dy*dy;
      if (d2 < bestDistSq) {
        bestDistSq = d2;
        best = e;
      }
    }
    const effectiveRange = this.range + gameState.upgrades.rangeBonus;
    if (best && bestDistSq <= effectiveRange*effectiveRange) return best;
    return null;
  }

  override onPreUpdate(engine: Engine, elapsedMs: number): void {
    // Slow HP regen based on game ticks: +1 HP per 10 ticks (no effect if dead)
    this.hpRegenTickCounter++;
    if (this.hpRegenTickCounter >= 20) {
      this.hpRegenTickCounter = 0;
      if (this.hp > 0 && this.hp < this.maxHp) {
        this.hp = Math.min(this.maxHp, this.hp + 1);
      }
    }

    // Mana regen (time-based)
    this.mana = Math.min(this.maxMana, this.mana + this.manaRegen * (elapsedMs/1000));
    
    // Keep the game state in sync
    gameState.mana = this.mana;
    gameState.maxMana = this.maxMana;
    gameState.maxHp = this.maxHp;
    gameState.hp = this.hp;

    // Rage timer tick
    if (this.rageActive) {
      this.rageTimeLeftMs -= elapsedMs;
      // Sync rage timer to game state for UI
      gameState.rageActive = true;
      gameState.rageMsLeft = Math.max(0, Math.floor(this.rageTimeLeftMs));
      gameState.rageMsTotal = Math.max(gameState.rageMsTotal, Math.floor(gameState.upgrades.rageDuration * 1000));
      if (this.rageTimeLeftMs <= 0) {
        this.rageActive = false;
        this.color = Color.Violet;
        gameState.rageActive = false;
        gameState.rageMsLeft = 0;
      }
    } else {
      // Ensure UI shows empty bar when not raging
      gameState.rageActive = false;
      gameState.rageMsLeft = 0;
    }

    // Attack
    this.attackCooldown -= elapsedMs/1000;
    if (this.attackCooldown <= 0 && !this.isKilled()) {
      const target = this.findNearestEnemy(engine);
      if (target) {
        this.shootAt(target, engine);
        const u = gameState.upgrades;
        // Proc rage on attack with mana cost (10 MP base, 5 MP if already raging)
        if (Math.random() < u.rageChance) {
          const cost = this.rageActive ? 5 : 10;
          if (this.mana >= cost) {
            this.mana -= cost;
            // Sync to game state immediately so UI reflects the spend
            gameState.mana = this.mana;
            this.rageActive = true;
            this.rageTimeLeftMs = Math.floor(u.rageDuration * 1000);
            // Initialize rage UI timers
            gameState.rageActive = true;
            gameState.rageMsTotal = this.rageTimeLeftMs;
            gameState.rageMsLeft = this.rageTimeLeftMs;
            this.color = Color.Red;
          }
        }
        const baseRate = this.attackRate * (1 + u.attackSpeedMult);
        const rate = baseRate * (this.rageActive ? 5 : 1);
        this.attackCooldown = 1 / Math.max(0.1, rate);
      }
    }

  }

  private shootAt(target: Actor, engine: Engine) {
    // Compute damage with crit
    const u = gameState.upgrades;
    let dmg = u.damage;
    let crit = false;
    if (Math.random() < u.critChance) {
      crit = true;
      dmg *= u.critMultiplier;
    }
    // Create a very simple projectile as a transient actor
    const dir = target.pos.sub(this.pos).normalize();
    const speed = 300; // px/s
    const proj = new Projectile(this.pos.clone(), dir, speed, dmg, crit, target, 0, 'base');
    engine.add(proj);
  }

}

// Lightweight projectile contained here to avoid circular deps
class Projectile extends Actor {
  dir: Vector;
  speed: number;
  damage: number;
  crit: boolean;
  target: Actor | null;
  hitCount = 0;
  origin: 'base' | 'split' | 'bounce';

  constructor(pos: Vector, dir: Vector, speed: number, damage: number, crit: boolean, target: Actor | null, hitCount: number, origin: 'base' | 'split' | 'bounce') {
    // Visual colors per request: split = red, bounce = white, base = yellow
    const col = origin === 'split' ? Color.Red : origin === 'bounce' ? Color.White : Color.Yellow;
    super({ pos, width: 4, height: 4, color: col, name: 'projectile' });
    this.dir = dir;
    this.speed = speed;
    this.damage = damage;
    this.crit = crit;
    this.target = target;
    this.hitCount = hitCount;
    this.origin = origin;
  }

  override onPreUpdate(engine: Engine, elapsedMs: number): void {
    // home a bit towards the current target
    if (this.target && !this.target.isKilled()) {
      const desired = this.target.pos.sub(this.pos).normalize();
      this.dir = this.dir.scale(0.8).add(desired.scale(0.2)).normalize();
    }
    this.pos = this.pos.add(this.dir.scale(this.speed * (elapsedMs/1000)));

    // Check hit against enemies using a simple radius check using liveEnemies set (avoid sqrt)
    const hitRadius = 10;
    const hitR2 = hitRadius * hitRadius;
    const sx = this.pos.x, sy = this.pos.y;
    for (const e of liveEnemies) {
      const dx = e.pos.x - sx;
      const dy = e.pos.y - sy;
      if (dx*dx + dy*dy < hitR2) {
        (e as any).takeDamage?.(this.damage);
        if (this.crit) {
          const popup = new DamagePopup(`${Math.round(this.damage)}!`, this.pos.clone());
          engine.add(popup);
        }
        this.handlePostHit(engine, e as Actor);
        this.kill();
        return;
      }
    }

    // remove if out of bounds (origin-centered bounds)
    const halfW = engine.drawWidth / 2;
    const halfH = engine.drawHeight / 2;
    if (this.pos.x < -halfW - 50 || this.pos.x > halfW + 50 || this.pos.y < -halfH - 50 || this.pos.y > halfH + 50) {
      this.kill();
    }
  }

  private handlePostHit(engine: Engine, hitEnemy: Actor) {
    const u = gameState.upgrades;

    // Split shot: only base-origin projectiles can split, and only on first hit
    if (this.origin === 'base' && this.hitCount === 0 && Math.random() < u.splitChance) {
      // find other enemies near the point of first impact (this.pos)
      const others: Actor[] = [];
      for (const e of liveEnemies) {
        const a = e as unknown as Actor;
        if (a !== hitEnemy) others.push(a);
      }
      // Limit split targets to within tower attack range measured from the impact point
      const effectiveRange = ((tower?.range ?? 0) + gameState.upgrades.rangeBonus);
      const inRange = others.filter(o => o.pos.distance(this.pos) <= effectiveRange);
      // pick up to splitCount nearest to receive splits
      inRange.sort((a, b) => a.pos.distance(this.pos) - b.pos.distance(this.pos));
      const count = Math.max(0, Math.floor(u.splitCount));
      const targets = inRange.slice(0, count);
      for (const o of targets) {
        const dir = o.pos.sub(this.pos).normalize();
        const dmg = this.damage * u.splitDiminishing;
        const p = new Projectile(this.pos.clone(), dir, this.speed, dmg, false, o as Actor, this.hitCount + 1, 'split');
        engine.add(p);
      }
    }

    // Bounce shot: split-origin projectiles never bounce; base and bounce can continue bouncing until cap
    if (this.origin !== 'split' && Math.random() < u.bounceChance && this.hitCount + 1 < u.bounceMaxTargets) {
      const candidates: Actor[] = [];
      for (const e of liveEnemies) {
        const a = e as unknown as Actor;
        if (a !== hitEnemy) candidates.push(a);
      }
      if (candidates.length > 0) {
        const next = candidates[Math.floor(Math.random() * candidates.length)];
        const dir = next.pos.sub(this.pos).normalize();
        const p = new Projectile(this.pos.clone(), dir, this.speed, this.damage * u.bounceDiminishing, false, next as Actor, this.hitCount + 1, 'bounce');
        engine.add(p);
      }
    }
  }
}

class DamagePopup extends Actor {
  label: Label;
  lifeMs = 600;
  constructor(text: string, pos: Vector) {
    super({ name: 'DamagePopup', pos, width: 1, height: 1, color: Color.Transparent });
    this.label = new Label({ text, pos: vec(0, 0), color: Color.Yellow, font: new Font({ size: 12, unit: FontUnit.Px }), anchor: vec(0.5, 1) });
    this.addChild(this.label);
  }

  override onPreUpdate(engine: Engine, elapsedMs: number): void {
    // float up and expire
    this.lifeMs -= elapsedMs;
    this.pos = this.pos.add(vec(0, -40 * (elapsedMs / 1000)));
    if (this.lifeMs <= 0) {
      this.kill();
    }
  }
}

export let tower: Tower;

function getVisualCenter(_engine: Engine): Vector {
  // World origin (0,0) is the tower position per new requirement
  return vec(0, 0);
}

export function recenterTower(engine: Engine) {
  if (tower) {
    tower.pos = getVisualCenter(engine);
  }
}

export function createTowerAtCenter(engine: Engine): Tower {
  // Place tower at the visual center accounting for DOM UI overlay
  const t = new Tower(getVisualCenter(engine));

  // Keep tower centered on any window/viewport resize
  const recenter = () => {
    t.pos = getVisualCenter(engine);
  };
  // Listen to both engine and screen resize events for robustness across Excalibur versions
  engine.on('postresize', recenter);
  engine.screen.events.on('resize', recenter);

  tower = t;
  return t;
}
